import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const VitalSchema = z.object({
  name: z.enum(["LCP", "CLS", "INP", "FCP", "TTFB"]),
  value: z.number().finite().min(0).max(600000),
  rating: z.enum(["good", "needs-improvement", "poor"]).optional(),
  id: z.string().max(120),
  path: z.string().max(300),
  navigationType: z.string().max(40).optional(),
  ua: z.string().max(400).optional(),
});

// "Poor" thresholds per metric — exceeding these is a regression candidate.
const POOR: Record<string, number> = { LCP: 4000, INP: 500, CLS: 0.25, FCP: 3000, TTFB: 1800 };
// Per (metric|path) rolling counter of poor samples in the current window.
const recentPoor = new Map<string, { count: number; firstAt: number; lastAlertAt: number }>();
const WINDOW_MS = 10 * 60_000;
const THRESHOLD = 5;
const COOLDOWN_MS = 30 * 60_000;

async function maybeAlertRegression(metric: string, path: string, value: number) {
  const key = `${metric}|${path}`;
  const now = Date.now();
  const e = recentPoor.get(key) ?? { count: 0, firstAt: now, lastAlertAt: 0 };
  if (now - e.firstAt > WINDOW_MS) { e.count = 0; e.firstAt = now; }
  e.count += 1;
  recentPoor.set(key, e);
  if (e.count < THRESHOLD) return;
  if (now - e.lastAlertAt < COOLDOWN_MS) return;
  e.lastAlertAt = now;

  const webhook = process.env.ALERT_WEBHOOK_URL;
  const base = process.env.APP_BASE_URL ?? "";
  const windowStart = new Date(e.firstAt).toISOString();
  const windowEnd = new Date(now).toISOString();
  const opsLink = `${base}/admin/quality`;
  // Persist to quality_alerts so admins see it in the UI even without a webhook configured.
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("quality_alerts").insert({
      source: "rum",
      severity: "warning",
      route: path,
      metric,
      value,
      threshold: POOR[metric] ?? null,
      window_start: windowStart,
      window_end: windowEnd,
      payload: { poor_samples: e.count, window_minutes: WINDOW_MS / 60000 } as never,
    });
  } catch (err) { console.error("[rum-alert] persist failed", err); }
  if (!webhook) return;
  try {
    await fetch(webhook, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        text: `:chart_with_downwards_trend: Web Vitals regression — ${metric} on ${path}`,
        attachments: [{
          fields: [
            { title: "Metric", value: metric, short: true },
            { title: "Latest value", value: String(value), short: true },
            { title: "Poor samples", value: `${e.count} in ${(WINDOW_MS / 60000) | 0}m`, short: true },
            { title: "Path", value: `${base}${path}`, short: false },
            { title: "Window", value: `${windowStart} → ${windowEnd}`, short: false },
            { title: "Quality dashboard", value: opsLink, short: false },
          ],
        }],
      }),
    });
  } catch (err) {
    console.error("[rum-alert] webhook failed", err);
  }
}

export const recordWebVital = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => VitalSchema.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("web_vitals").insert({
      name: data.name,
      value: data.value,
      rating: data.rating ?? null,
      metric_id: data.id,
      path: data.path,
      navigation_type: data.navigationType ?? null,
      user_agent: data.ua ?? null,
    });
    // SECURITY: do NOT trigger external webhook / quality_alerts insert from this
    // unauthenticated endpoint. Regression alerting is owned by an admin-only
    // cron path (rumAlertSweep) that aggregates from web_vitals.
    return { ok: true };
  });

// Admin / cron-callable sweep that detects poor-vital regressions by querying
// the web_vitals table directly. Safe to call from an authenticated admin or
// from a pg_cron-driven public route that enforces its own apikey check.
export const rumAlertSweep = createServerFn({ method: "POST" })
  .handler(async () => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const since = new Date(Date.now() - WINDOW_MS).toISOString();
    const { data } = await supabaseAdmin
      .from("web_vitals")
      .select("name, path, value")
      .gte("created_at", since);
    const groups = new Map<string, { metric: string; path: string; count: number; max: number }>();
    for (const row of (data ?? []) as Array<{ name: string; path: string; value: number }>) {
      const t = POOR[row.name];
      if (!t || row.value <= t) continue;
      const key = `${row.name}|${row.path}`;
      const g = groups.get(key) ?? { metric: row.name, path: row.path, count: 0, max: 0 };
      g.count += 1; g.max = Math.max(g.max, row.value);
      groups.set(key, g);
    }
    let alerted = 0;
    for (const g of groups.values()) {
      if (g.count < THRESHOLD) continue;
      await maybeAlertRegression(g.metric, g.path, g.max);
      alerted += 1;
    }
    return { ok: true, alerted };
  });
